import { useEffect, useState } from 'react';

import type { MusicAnalysis } from '@/utils/arcade/musicAnalysis';
const cache = new Map<string, MusicAnalysis>();
const MAX_CACHED = 24;

// Only analyse the audible preview, never the entire endless queue. The audio
// clock can keep using spectral onsets until this stronger tempo estimate lands.
export function usePreviewAnalysis(url: string | undefined): MusicAnalysis | null {
  const [result, setResult] = useState<{ url: string; analysis: MusicAnalysis } | null>(null);
  useEffect(() => {
    if (
      !url ||
      cache.has(url) ||
      typeof OfflineAudioContext === 'undefined' ||
      typeof Worker === 'undefined'
    )
      return;
    const controller = new AbortController();
    let worker: Worker | null = null;
    let active = true;
    const timeout = setTimeout(() => {
      controller.abort();
      worker?.terminate();
      active = false;
    }, 20000);
    void (async () => {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) {
        clearTimeout(timeout);
        return;
      }
      // music-tempo's default hop time assumes 44.1 kHz. decodeAudioData
      // resamples to the offline context's rate, including 48 kHz previews.
      const context = new OfflineAudioContext(1, 1, 44100);
      const buffer = await context.decodeAudioData(await response.arrayBuffer());
      if (!active) return;
      const length = Math.min(buffer.length, 44100 * 45);
      const mono = new Float32Array(length);
      for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
        const data = buffer.getChannelData(channel);
        for (let i = 0; i < length; i++) mono[i] += data[i] / buffer.numberOfChannels;
      }
      worker = new Worker(new URL('../../utils/arcade/previewTempo.worker.ts', import.meta.url), {
        type: 'module',
      });
      worker.onmessage = (event: MessageEvent<MusicAnalysis | null>) => {
        if (active && event.data !== null) {
          cache.set(url, event.data);
          if (cache.size > MAX_CACHED) cache.delete(cache.keys().next().value!);
          setResult({ url, analysis: event.data });
        }
        clearTimeout(timeout);
        worker?.terminate();
      };
      worker.onerror = () => {
        clearTimeout(timeout);
        worker?.terminate();
      };
      worker.postMessage(mono, [mono.buffer]);
    })().catch(() => {
      clearTimeout(timeout); /* Offline or undecodable audio keeps the live tracker. */
    });
    return () => {
      active = false;
      clearTimeout(timeout);
      controller.abort();
      worker?.terminate();
    };
  }, [url]);
  return url ? (cache.get(url) ?? (result?.url === url ? result.analysis : null)) : null;
}

export function usePreviewTempo(url: string | undefined): number | null { return usePreviewAnalysis(url)?.bpm ?? null; }
